import { GAME_POTIONS, METAMORPH_POTION_ID, DEMI_LUNE_POTION_ID } from "./potions";
import { GEM_FILES, isPreciousGem } from "./gems";
import { getKeyById } from "./keys";
import trefleMajeurImg from "../assets/trefle_majeur.png";

const publicUrl = process.env.PUBLIC_URL || "";
const CLOVER_MAJEUR_POTION_ID = "trefle-majeur";

export const REWARD_WHEEL_TYPES = {
  ENCOURAGEMENT: "encouragement",
  FELICITATION: "felicitation",
};

export const REWARD_WHEEL_SEGMENT_COUNT = 10;
export const REWARD_WHEEL_SEGMENT_ANGLE = 360 / REWARD_WHEEL_SEGMENT_COUNT;

const potionsById = new Map(GAME_POTIONS.map((potion) => [potion.id, potion]));

const COMMON_GEM_IDS = GEM_FILES.map((filename) =>
  filename.replace(/\.[^.]+$/, "")
).filter((gemId) => !isPreciousGem(gemId));

const FELICITATION_RARE_GEM_IDS = ["émeraude", "saphir", "rubis", "diamant"];
const FELICITATION_CLOVER_MAJEUR_COUNTS = [1, 2, 3, 5];
const WHEEL_KEY_IDS = ["melusine", "loudiana", "eurydice", "zamselly", "melitaea"];

const WHEEL_REWARD_POTION_IDS = [
  "decouverte",
  "bouillon-deja-vu",
  "mixture-3eme-oeil",
  "grog-veuve-noire",
  "mixture-du-brucolaque",
  "serum-cadran",
  "remede-ignorance",
  "philtre-mnesique",
  "elixir-clarte",
  "baume-sursis",
];

const FELICITATION_WHEEL_REWARD_POTION_IDS = [
  ...WHEEL_REWARD_POTION_IDS,
  METAMORPH_POTION_ID,
  DEMI_LUNE_POTION_ID,
];

function buildGemImageUrl(gemId) {
  return `${publicUrl}/gems/${encodeURIComponent(`${gemId}.png`)}`;
}

function buildPotionSegment(potionId, count = 1) {
  const potion = potionsById.get(potionId);
  return {
    type: "potion",
    potionId,
    count,
    imageUrl: potion?.image,
    label: count > 1 ? `${count} × ${potion?.name ?? potionId}` : potion?.name ?? potionId,
    shortLabel: potion?.name ?? potionId,
  };
}

function buildGemSegment(gemId, count = 1) {
  return {
    type: "gem",
    gemId,
    count,
    imageUrl: buildGemImageUrl(gemId),
    label:
      count > 1
        ? `${count} pierres ${isPreciousGem(gemId) ? "rares" : "communes"} (${humanizeGemShort(gemId)})`
        : `1 pierre ${isPreciousGem(gemId) ? "rare" : "commune"} (${humanizeGemShort(gemId)})`,
    shortLabel: humanizeGemShort(gemId),
  };
}

function humanizeGemShort(gemId) {
  if (!gemId) return "";
  return gemId.charAt(0).toUpperCase() + gemId.slice(1);
}

/** Modèle roue d'encouragement — pierres tirées à l'ouverture, potions fixes. */
export const ENCOURAGEMENT_WHEEL_SEGMENTS = [
  { id: "encouragement-0", type: "gem", count: 1, label: "1 pierre commune" },
  { id: "encouragement-1", type: "gem", count: 2, label: "2 pierres communes" },
  { id: "encouragement-2", type: "gem", count: 3, label: "3 pierres communes" },
  buildPotionSegment("remede-ignorance"),
  buildPotionSegment("baume-sursis"),
  buildPotionSegment("serum-cadran"),
  buildPotionSegment("bouillon-deja-vu"),
  { id: "encouragement-7", type: "gem", count: 1, label: "1 pierre commune" },
  { id: "encouragement-8", type: "gem", count: 1, label: "1 pierre commune" },
  { id: "encouragement-9", type: "gem", count: 1, label: "1 pierre commune" },
].map((segment, index) => ({
  ...segment,
  id: segment.id ?? `encouragement-${index}`,
}));

/**
 * Roue de félicitation — visuels distincts ; le lot exact est tiré côté serveur
 * selon le tier de chaque portion.
 */
export const FELICITATION_WHEEL_SEGMENTS = [
  {
    id: "felicitation-rare-5",
    type: "gem",
    prizeTier: "rare-five",
    gemId: "rubis",
    count: 5,
    imageUrl: buildGemImageUrl("rubis"),
    label: "5 pierres rares",
    shortLabel: "5 pierres rares",
  },
  {
    id: "felicitation-common-5",
    type: "gem",
    prizeTier: "common-five",
    gemId: "pyrite",
    count: 5,
    imageUrl: buildGemImageUrl("pyrite"),
    label: "5 pierres communes",
    shortLabel: "5 pierres communes",
  },
  {
    id: "felicitation-common-3",
    type: "gem",
    prizeTier: "common-three",
    gemId: "citrine",
    count: 3,
    imageUrl: buildGemImageUrl("citrine"),
    label: "3 pierres communes",
    shortLabel: "3 pierres communes",
  },
  {
    id: "felicitation-common-2a",
    type: "gem",
    prizeTier: "common-two",
    gemId: "améthyste",
    count: 2,
    imageUrl: buildGemImageUrl("améthyste"),
    label: "2 pierres communes",
    shortLabel: "2 pierres communes",
  },
  {
    id: "felicitation-common-2b",
    type: "gem",
    prizeTier: "common-two",
    gemId: "topaze",
    count: 2,
    imageUrl: buildGemImageUrl("topaze"),
    label: "2 pierres communes",
    shortLabel: "2 pierres communes",
  },
  {
    id: "felicitation-potion-3a",
    type: "potion",
    prizeTier: "potion-three",
    potionId: "decouverte",
    count: 3,
    imageUrl: potionsById.get("decouverte")?.image,
    label: "3 potions",
    shortLabel: "3 potions",
  },
  {
    id: "felicitation-potion-3b",
    type: "potion",
    prizeTier: "potion-three",
    potionId: "bouillon-deja-vu",
    count: 3,
    imageUrl: potionsById.get("bouillon-deja-vu")?.image,
    label: "3 potions",
    shortLabel: "3 potions",
  },
  {
    id: "felicitation-key",
    type: "key",
    prizeTier: "key-one",
    keyId: "melusine",
    count: 1,
    imageUrl: getKeyById("melusine")?.image,
    label: "1 parchemin",
    shortLabel: "Parchemin",
  },
  {
    id: "felicitation-clover-majeur",
    type: "potion",
    prizeTier: "clover-majeur",
    potionId: CLOVER_MAJEUR_POTION_ID,
    count: 1,
    imageUrl: trefleMajeurImg,
    label: "Potion de trèfles (majeure)",
    shortLabel: "Potion de trèfles (majeure)",
  },
  {
    id: "felicitation-potion-1a",
    type: "potion",
    prizeTier: "potion-one",
    potionId: "mixture-3eme-oeil",
    count: 1,
    imageUrl: potionsById.get("mixture-3eme-oeil")?.image,
    label: "1 potion",
    shortLabel: "1 potion",
  },
];

/** @deprecated Utiliser getWheelSegments(wheelType) */
export const REWARD_WHEEL_SEGMENTS = ENCOURAGEMENT_WHEEL_SEGMENTS;

const ENCOURAGEMENT_SEGMENT_WEIGHTS = [
  { index: 0, weight: 10 },
  { index: 1, weight: 10 },
  { index: 2, weight: 10 },
  { index: 3, weight: 10 },
  { index: 4, weight: 10 },
  { index: 5, weight: 10 },
  { index: 6, weight: 10 },
  { index: 7, weight: 10 },
  { index: 8, weight: 10 },
  { index: 9, weight: 10 },
];

export function getWheelSegments(wheelType) {
  if (wheelType === REWARD_WHEEL_TYPES.FELICITATION) {
    return FELICITATION_WHEEL_SEGMENTS;
  }
  return ENCOURAGEMENT_WHEEL_SEGMENTS;
}

/** Segments avec visuels complets pour l'aperçu (avant appel API start). */
export function hydrateWheelSegmentsForPreview(segments) {
  return segments.map((segment) => {
    if (segment.type === "gem") {
      const gemId = segment.gemId || "pyrite";
      return {
        ...segment,
        gemId,
        imageUrl: segment.imageUrl || buildGemImageUrl(gemId),
      };
    }

    if (segment.type === "key") {
      const key = getKeyById(segment.keyId);
      return {
        ...segment,
        imageUrl: segment.imageUrl || key?.image,
      };
    }

    const potion = potionsById.get(segment.potionId);
    return {
      ...segment,
      imageUrl: segment.imageUrl || potion?.image,
    };
  });
}

export function getPreviewWheelSegments(wheelType) {
  return hydrateWheelSegmentsForPreview(getWheelSegments(wheelType));
}

export function getWheelTitle(wheelType) {
  return wheelType === REWARD_WHEEL_TYPES.FELICITATION
    ? "Roue de félicitation"
    : "Roue d'encouragement";
}

export const REWARD_WHEEL_CLOSE_HINT = "Vous pouvez maintenant fermer cette fenêtre";
export const REWARD_WHEEL_CLOSE_HINT_DELAY_MS = 1000;

export function getSegmentCenterAngle(segmentIndex) {
  return segmentIndex * REWARD_WHEEL_SEGMENT_ANGLE + REWARD_WHEEL_SEGMENT_ANGLE / 2;
}

export function getSegmentPolarPosition(segmentIndex, radiusPx, rotorSize = 320) {
  return getSegmentPolarPositionAtRotation(segmentIndex, 0, radiusPx, rotorSize);
}

export function getSegmentPolarPositionAtRotation(
  segmentIndex,
  wheelRotation,
  radiusPx,
  rotorSize = 320
) {
  const angleDeg = getSegmentCenterAngle(segmentIndex) + wheelRotation;
  const angleRad = (angleDeg * Math.PI) / 180;
  const radiusPct = (radiusPx / rotorSize) * 100;
  return {
    left: 50 + Math.sin(angleRad) * radiusPct,
    top: 50 - Math.cos(angleRad) * radiusPct,
    visualAngle: angleDeg,
  };
}

export const REWARD_WHEEL_SPIN_DURATION_MS = 4200;
/** Pause après chaque tour (batch) pour laisser le segment gagnant clignoter. */
export const REWARD_WHEEL_RESULT_REVEAL_MS = 2600;

export function easeOutWheelSpin(t) {
  return 1 - (1 - t) ** 3;
}

export function animateWheelRotation(from, to, durationMs, onUpdate) {
  return new Promise((resolve) => {
    const start = performance.now();

    function frame(now) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / durationMs, 1);
      const eased = easeOutWheelSpin(progress);
      const current = from + (to - from) * eased;
      onUpdate(current);

      if (progress < 1) {
        requestAnimationFrame(frame);
      } else {
        resolve(current);
      }
    }

    requestAnimationFrame(frame);
  });
}

export function pickEncouragementSegmentIndex() {
  const totalWeight = ENCOURAGEMENT_SEGMENT_WEIGHTS.reduce(
    (sum, entry) => sum + entry.weight,
    0
  );
  let roll = Math.random() * totalWeight;

  for (const entry of ENCOURAGEMENT_SEGMENT_WEIGHTS) {
    roll -= entry.weight;
    if (roll <= 0) {
      return entry.index;
    }
  }

  return ENCOURAGEMENT_SEGMENT_WEIGHTS[ENCOURAGEMENT_SEGMENT_WEIGHTS.length - 1].index;
}

export function pickFelicitationSegmentIndex() {
  return Math.floor(Math.random() * REWARD_WHEEL_SEGMENT_COUNT);
}

export function pickWheelSegmentIndex(wheelType) {
  if (wheelType === REWARD_WHEEL_TYPES.FELICITATION) {
    return pickFelicitationSegmentIndex();
  }
  return pickEncouragementSegmentIndex();
}

/** @deprecated Utiliser pickWheelSegmentIndex(wheelType) */
export function pickRandomWheelSegmentIndex() {
  return pickEncouragementSegmentIndex();
}

export function computeWheelRotation(segmentIndex, currentRotation = 0) {
  const jitter = (Math.random() - 0.5) * (REWARD_WHEEL_SEGMENT_ANGLE - 8);
  const segmentCenter = getSegmentCenterAngle(segmentIndex) + jitter;
  const targetMod = (360 - segmentCenter + 360) % 360;
  const currentMod = ((currentRotation % 360) + 360) % 360;
  const fullSpins = 5 + Math.floor(Math.random() * 3);
  let delta = targetMod - currentMod;
  if (delta <= 0) delta += 360;
  return currentRotation + fullSpins * 360 + delta;
}

export function formatWheelPrize(segment) {
  if (!segment) return "";
  if (segment.type === "gem") {
    const rarityLabel = isPreciousGem(segment.gemId) ? "rare" : "commune";
    if (segment.count <= 1) {
      return `1 pierre ${rarityLabel} (${segment.shortLabel})`;
    }
    return `${segment.count} pierres ${rarityLabel}s (${segment.shortLabel})`;
  }
  if (segment.count > 1) {
    return `${segment.count} × ${segment.shortLabel}`;
  }
  if (segment.type === "key") {
    return segment.label;
  }
  return segment.label;
}

export function hydrateWheelSegmentsFromApi(apiSegments) {
  return apiSegments.map((segment) => {
    if (segment.type === "gem") {
      return {
        ...segment,
        imageUrl: buildGemImageUrl(segment.gemId),
      };
    }

    if (segment.type === "key") {
      const key = getKeyById(segment.keyId);
      return {
        ...segment,
        count: 1,
        imageUrl: key?.image,
        shortLabel: key?.name ? `Parchemin ${key.name}` : segment.label,
      };
    }

    const potion = potionsById.get(segment.potionId);
    const isCloverMajeur = segment.potionId === CLOVER_MAJEUR_POTION_ID;
    return {
      ...segment,
      imageUrl: isCloverMajeur ? trefleMajeurImg : potion?.image,
      shortLabel: isCloverMajeur
        ? "Potion de trèfles (majeure)"
        : potion?.name ?? segment.label,
    };
  });
}

export function formatResolvedWheelPrize(prize) {
  if (!prize) return "";
  if (prize.type === "gem") {
    const rarityLabel = isPreciousGem(prize.gemId) ? "rare" : "commune";
    const gemName = humanizeGemShort(prize.gemId);
    if (prize.count <= 1) {
      return `1 pierre ${rarityLabel} (${gemName})`;
    }
    return `${prize.count} pierres ${rarityLabel}s (${gemName})`;
  }
  if (prize.type === "key") {
    return prize.label;
  }
  const name = prize.shortLabel || prize.label;
  if (prize.count > 1) {
    return `${prize.count} × ${name}`;
  }
  return name;
}

export function getWheelPrizeKey(prize) {
  if (!prize) return "";
  if (prize.type === "gem") return `gem:${prize.gemId}`;
  if (prize.type === "key") return `key:${prize.keyId ?? prize.label}`;
  if (prize.potionId) return `potion:${prize.potionId}`;
  return prize.label || "unknown";
}

export function hydrateWheelPrize(prize) {
  if (!prize) return null;
  if (prize.imageUrl) return prize;

  if (prize.type === "gem") {
    return { ...prize, imageUrl: buildGemImageUrl(prize.gemId) };
  }

  if (prize.type === "key") {
    const key = getKeyById(prize.keyId);
    return { ...prize, imageUrl: key?.image };
  }

  const potion = potionsById.get(prize.potionId);
  const isCloverMajeur = prize.potionId === CLOVER_MAJEUR_POTION_ID;
  return {
    ...prize,
    imageUrl: isCloverMajeur ? trefleMajeurImg : potion?.image,
  };
}

export function aggregateWheelPrizes(prizes) {
  const totals = new Map();

  for (const raw of prizes) {
    const prize = hydrateWheelPrize(raw);
    if (!prize) continue;

    const key = getWheelPrizeKey(prize);
    const amount = prize.count ?? 1;
    const existing = totals.get(key);

    if (existing) {
      existing.count += amount;
    } else {
      totals.set(key, { ...prize, count: amount });
    }
  }

  return Array.from(totals.values());
}

export {
  COMMON_GEM_IDS,
  FELICITATION_CLOVER_MAJEUR_COUNTS,
  FELICITATION_RARE_GEM_IDS,
  FELICITATION_WHEEL_REWARD_POTION_IDS,
  WHEEL_KEY_IDS,
  WHEEL_REWARD_POTION_IDS,
};
